import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import type { AlineEditor, AlineSelection } from '@aline/common';
import type { YHistoryEditor } from '@slate-yjs/core';
import type { BaseEditor, Editor } from 'slate';
import {
  useMaybeEditorRuntime,
  useRequiredEditorRuntime,
} from 'src/editor/editor-runtime-registry';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { useMaybeEditorUiState } from 'src/editor/editor-ui-state-registry';
import type { HistoryEditor } from 'src/layout-engine/plugins/history';

export type AlineClientEditor = AlineEditor & (HistoryEditor | YHistoryEditor);

export const PageElContext = createContext<HTMLElement | null>(null);
export const EditorOnChangeCounterContext = createContext<number>(0);

/**
 * Get the editor for the current runtime scope. Inside the editor subtree this is provided by
 * AlineEditor. For other parts of the application, such as the right drawer, this is scoped by the
 * tab ID.
 */
export function useEditor(): AlineClientEditor {
  return useRequiredEditorRuntime().editor;
}

/**
 * Use editor satisfying the given assert func. Throws if the editor does not satisfy the assert func.
 */
export function useTypedEditor<T extends BaseEditor>(
  assertFn: (editor: BaseEditor) => editor is T
): T & AlineClientEditor {
  const editor = useEditor();
  if (!assertFn(editor)) throw new Error('The editor does not match the expected type.');

  return editor;
}

export function useIsEditorVariant(assertFn: (editor: BaseEditor) => boolean) {
  const editor = useEditor();
  return assertFn(editor);
}

export function createSafeEditorVariantHook<
  TParams extends unknown[],
  TReturn,
  TFallbackParams extends [TReturn] | [],
>(
  assertFn: (editor: BaseEditor) => boolean,
  hook: (...parmas: TParams) => TReturn,
  ...fallbackParam: TFallbackParams
): (
  ...params: TParams
) => TReturn | (TFallbackParams extends [infer TFallback] ? TFallback : undefined) {
  return (...params: TParams) => {
    const editor = useEditor();
    if (!assertFn(editor)) {
      return fallbackParam[0] as TFallbackParams extends [infer TFallback] ? TFallback : undefined;
    }

    return hook(...params);
  };
}

/**
 * Get the current editor instance. Returns null when outside the editor.
 * Backed by a reactive var so it works outside of the Slate component tree.
 */
export function useMaybeEditor(): AlineClientEditor | null {
  return useMaybeEditorRuntime()?.editor ?? null;
}

/**
 * Get the current editor only when it hosts the given document. The ambient
 * runtime can belong to a different document than the caller's subject — a
 * chat-hosted panel about one document can render beside another document's
 * editor — and reading content or applying edits through a mismatched editor
 * targets the wrong document, so a mismatch reads the same as no editor.
 */
export function useMaybeEditorForDocument(documentId: UUID): AlineClientEditor | null {
  const runtime = useMaybeEditorRuntime();
  return runtime && runtime.documentId === documentId ? runtime.editor : null;
}

/**
 * Returns whether the editor is loading (e.g., loading a new doc).
 * Backed by a reactive var so it works outside of the Slate component tree.
 */
export function useEditorLoading(): boolean {
  return useMaybeEditorRuntime()?.editorLoading ?? true;
}

/**
 * Returns the DOM node of the current page.
 */
export function usePageEl() {
  return useContext(PageElContext);
}

/**
 * Get the current selection.
 */
export function useSelection(): AlineSelection | null {
  return useMaybeEditorUiState()?.selection ?? null;
}

/**
 * Get the current `focused` state of the editor.
 */
export function useFocused(): boolean {
  return useMaybeEditorUiState()?.focused ?? false;
}

/**
 * A set of functions used by useSlateSelector that are called whenever the editor value changes.
 */
const slateSelectorsByEditor = new WeakMap<Editor, Set<() => void>>();

export function updateSlateSelectors(editor: Editor) {
  getSlateSelectorsForEditor(editor).forEach(selector => selector());
}

function getSlateSelectorsForEditor(editor: Editor) {
  let slateSelectors = slateSelectorsByEditor.get(editor);
  if (!slateSelectors) {
    slateSelectors = new Set();
    slateSelectorsByEditor.set(editor, slateSelectors);
  }
  return slateSelectors;
}

/**
 * A version of Slate's useSlateSelector hook that works with Aline, since the original hook
 * depends on slate-react components that we're not using. Use this hook whenever you need to
 * subscribe to some query that is not a pure function of the arguments.
 *
 * For example, any query that depends on the editor object is not a pure function, since the
 * editor's state can change without replacing the editor object with a new instance. This means
 * that without useSlateSelector, React has no way of knowing when the data needs to be recomputed.
 *
 * Always return primitive values from the selector if possible, which will prevent this hook from
 * causing unnecessary re-renders. If a non-primitive value such as an object or array must be
 * returned, pass a custom equality function such as ramda's equals function.
 */
export function useSlateSelector<T>(
  selector: (editor: AlineClientEditor) => T,
  equals: (a: T, b: T) => boolean
): T;
// Allow omitting the equals argument only if the selector returns a primitive value
export function useSlateSelector<T extends string | number | boolean | null | undefined>(
  selector: (editor: AlineClientEditor) => T,
  equals?: (a: T, b: T) => boolean
): T;
export function useSlateSelector<T>(
  selector: (editor: AlineClientEditor) => T,
  equals = shallowEquals
): T {
  const editor = useEditor();
  const evaluateSelector = useCallback(() => selector(editor), [selector, editor]);
  const [value, setValue] = useState(evaluateSelector);

  /** Update the value if the value returned by the selector is new according to equals. */
  const maybeUpdateSelector = useCallback(() => {
    const newValue = evaluateSelector();
    if (!equals(newValue, value)) {
      setValue(newValue);
    }
  }, [evaluateSelector, value, equals]);

  /**
   * Re-evaluate the selector if it has changed. Mutating a ref from a render function is
   * discouraged by React. However, using a more idiomatic useState-based usePrevious hook is worse
   * in this case because would result in infinite re-rendering if a non-memoized selector is passed
   * to it.
   */
  const prevSelector = useRef(selector);
  if (selector !== prevSelector.current) {
    prevSelector.current = selector;
    maybeUpdateSelector();
  }

  // Register a callback that will run whenever the editor value changes
  useEffect(() => {
    const slateSelectors = getSlateSelectorsForEditor(editor);
    slateSelectors.add(maybeUpdateSelector);
    return () => {
      slateSelectors.delete(maybeUpdateSelector);
    };
  }, [editor, maybeUpdateSelector]);

  return value;
}

function shallowEquals<T>(a: T, b: T) {
  return a === b;
}

/**
 * Subscribes to editor updates. Returns a counter that increments on each update.
 * Used to trigger re-renders when the editor.renderModel changes.
 */
export function useEditorOnChangeCounter(): number {
  return useContext(EditorOnChangeCounterContext);
}

export function useHeaderOrFooterOpen(type: 'header' | 'footer') {
  const isHeader = type === 'header';
  return useEditorTabStateSelector(state =>
    isHeader ? state.documentUi.headerOpen : state.documentUi.footerOpen
  );
}
